import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn } from "lucide-react";
import FbmLogo from "@/components/FbmLogo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxmb290YmFsbCUyMHBpdGNoJTIwc3RhZGl1bSUyMGxpZ2h0c3xlbnwwfHx8fDE3ODEwNzc4MTR8MA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/scout"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name}`);
      navigate(u.role === "admin" ? "/admin" : "/scout", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-theme min-h-screen w-full grid lg:grid-cols-2 bg-background text-foreground">
      {/* Left: Form */}
      <div className="flex flex-col justify-between p-8 md:p-12">
        <div className="flex items-center gap-3">
          <FbmLogo size={36} withText={false} />
          <div className="font-display uppercase tracking-[0.18em] text-sm text-foreground">
            FBM · Football Behavior Management
          </div>
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0 lg:my-12 py-12">
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">
            Phase 01 · Control Room
          </div>
          <h1 className="font-display uppercase text-5xl md:text-6xl font-bold leading-[0.95] tracking-tight">
            Sign in to <br />
            <span className="text-primary">the bench.</span>
          </h1>
          <p className="text-muted-foreground mt-5 max-w-sm">
            Manage your scouting network — organizations, scouts and access. Built for clubs, agencies, journalists and agents.
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@club.com"
                className="bg-transparent border-0 border-b border-border rounded-none px-0 h-12 text-lg focus-visible:ring-0 focus-visible:border-primary"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-transparent border-0 border-b border-border rounded-none px-0 h-12 text-lg focus-visible:ring-0 focus-visible:border-primary pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-password-visibility"
                  aria-label="Toggle password visibility"
                >
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-none font-display uppercase tracking-[0.18em] text-base"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : (
                <span className="inline-flex items-center gap-2"><LogIn className="h-4 w-4" /> Sign in</span>
              )}
            </Button>
          </form>
        </div>

        <div className="text-xs text-muted-foreground leading-relaxed max-w-md">
          © {new Date().getFullYear()} Football Behavior Management BV, Binderij 7-L, 1185 ZH Amstelveen, the Netherlands, Chamber of Commerce: 70579652
        </div>
      </div>

      {/* Right: Hero */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Stadium under night lights"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70 pitch-stripes" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-4">// Scouting Network</div>
          <div className="font-display uppercase text-4xl xl:text-5xl leading-[0.95] text-white max-w-md">
            Eyes on every pitch.<br />
            Stats on every player.
          </div>
          <div className="mt-6 text-white/60 text-sm max-w-md">
            A single command center for clubs, agencies and agents to manage their scouting force.
          </div>
        </div>
      </div>
    </div>
  );
}
